/**
 * Hand-written wrapper for the Calculator v3 Keep-vs-Outsource endpoint.
 * Collapses to the generated client once BE lands in dev.
 */
import axiosInstance from './axios';

export type StrategicValue =
  | 'gateway'
  | 'industry_entry'
  | 'multi_site'
  | 'standard';

export interface KeepScenarioDto {
  annualPrice: number;
  directLaborCost: number;
  hoursPerYear: number;
  monthlyValue: number;
  totalCost: number;
}

export interface SubAnalysisRequest {
  contractDurationMonths?: number;
  currentCapacityUtilization?: number;
  keep: KeepScenarioDto;
  oversightCostPerYear?: number;
  requiredSkillIds?: string[];
  strategicValue: StrategicValue;
  tenantMarkupMultiplier?: number;
}

export interface KeepScenarioResult {
  adjustedMargin?: number;
  annualCost: number;
  annualPrice: number;
  capacityImpactHours: number;
  driveCostPerYear: number;
  effectiveHourlyRate: number;
  margin: number;
  opportunityCostPerYear?: number;
}

export interface OutsourceScenarioResult {
  annualCost: number;
  annualPrice: number;
  capacityImpactHours: 0;
  effectiveHourlyRate: number;
  margin: number;
  overheadCost: number;
  oversightCostPerYear: number;
  partnerBilledCost: number;
  partnerFitScore: number;
  partnerId: string;
  partnerName: string;
  riskScore: number;
}

export interface SubAnalysisResult {
  bestOutsourceIndex: number;
  configGaps: string[];
  keep: KeepScenarioResult;
  marginAdvantage: number;
  outsource: OutsourceScenarioResult[];
  recommendation: 'keep' | 'outsource';
  recommendationReason: string;
}

const preview = async (
  request: SubAnalysisRequest,
): Promise<SubAnalysisResult> => {
  const { data } = await axiosInstance.post<SubAnalysisResult>(
    '/sub-analysis/preview',
    request,
  );
  return data;
};

export interface ShiftAnalysisRequest {
  keep: KeepScenarioDto;
  oversightCostPerYear?: number;
  requiredSkillIds?: string[];
  shiftId: string;
  shiftName?: string;
}

export interface SubAnalysisMultiRequest {
  contractDurationMonths?: number;
  currentCapacityUtilization?: number;
  overheadPercent?: number;
  shifts: ShiftAnalysisRequest[];
  strategicValue: StrategicValue;
}

export interface ShiftAnalysisResult {
  analysis: SubAnalysisResult;
  shiftId: string;
  shiftName?: string;
}

export interface MultiShiftRollup {
  netToTenantIfRecommended: number;
  savingsVsAllKeep: number;
  shiftsRecommendedKeep: number;
  shiftsRecommendedOutsource: number;
  totalAnnualPrice: number;
  totalIfAllOutsourced: number;
  totalIfRecommended: number;
  totalKeepCost: number;
}

export interface SubAnalysisMultiResult {
  rollup: MultiShiftRollup;
  shifts: ShiftAnalysisResult[];
}

const previewMulti = async (
  request: SubAnalysisMultiRequest,
): Promise<SubAnalysisMultiResult> => {
  const { data } = await axiosInstance.post<SubAnalysisMultiResult>(
    '/sub-analysis/preview-multi',
    request,
  );
  return data;
};

const subAnalysisApi = { preview, previewMulti };

export default subAnalysisApi;
